import { useTranslation } from "react-i18next";
import { secondMs } from "verdi-common/timeConstants";
import { exportDataStreamsCsv } from "@/api/orval/generated/data-stream";
import { Button } from "@/components/ui/button";
import { toastManager } from "@/components/ui/toast";
import { useUnitPreferences } from "@/hooks/use-unit-preferences";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, iconRegistry } from "@/icons/icon-registry";
import { useDataViewerStore } from "../data-viewer-provider";
import { useSpec } from "../spec/spec-context";
import { buildCsvExportBody, csvExportErrorKind, csvExportFilename } from "./build-csv-export-body";

const DownloadIcon = iconRegistry[ICON_KEYS.DOWNLOAD];

function saveCsv(filename: string, csv: string) {
    // The client reads the body as text, which drops the server's BOM; Excel needs it to read UTF-8.
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoking in the same tick can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), secondMs);
}

export function DownloadCsvButton() {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "download-csv" });
    const { spec } = useSpec();
    const { unitPresetKey } = useUnitPreferences();
    const startMs = useDataViewerStore((state) => state.startMs);
    const endMs = useDataViewerStore((state) => state.endMs);
    const unitOverrides = useDataViewerStore((state) => state.unitOverrides);
    const isExporting = useDataViewerStore((state) => state.isCsvExporting);
    const beginCsvExport = useDataViewerStore((state) => state.beginCsvExport);
    const endCsvExport = useDataViewerStore((state) => state.endCsvExport);
    // In panel order. The API rejects repeated ids.
    const streamIds = [...new Set(spec.panels.flatMap((panel) => panel.streams.map((stream) => stream.streamId)))];

    const handleDownload = async () => {
        if (!beginCsvExport()) return;
        try {
            const body = buildCsvExportBody({
                streamIds,
                startMs,
                endMs,
                unitPresetKey,
                unitOverrides,
                timeZone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
            const csv = await exportDataStreamsCsv(body);
            if (!csv) throw new Error("CSV response body is empty");
            saveCsv(csvExportFilename(startMs, endMs), csv);
        } catch (error) {
            toastManager.add({ title: t(`errors.${csvExportErrorKind(error)}`), type: "error" });
        } finally {
            endCsvExport();
        }
    };

    return (
        <Button
            aria-label={t("label")}
            disabled={streamIds.length === 0}
            loading={isExporting}
            onClick={handleDownload}
            size={"icon"}
            tooltip={t("label")}
            variant={"outline"}
        >
            <DownloadIcon className={"size-[14px]"} />
        </Button>
    );
}
